import { DraftShape, Shape, Storey } from '@shared/models/design/storey.model';
import { Building } from '@shared/models/design/building-design.model';
import { Point2D } from '@shared/models/geometry.model';
import { Coordinate } from '@shared/models/geography.model';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

export type PointDragSnapGuides = {
  origin: Point2D;
  directions: Point2D[];
};

export type PointPayload = {
  /** Original adjacent-segment axes shown while Shift constrains a point drag. */
  pointDragSnapGuides?: PointDragSnapGuides;
  /**
   * Call to prevent the default behaviour of the originating map event
   */
  preventDefault: () => void;

  /**
   * Screen position of the interaction in pixel coordinates
   */
  screenPoint: Point2D;

  /**
   * Geographic position of the interaction
   */
  coordinate: Coordinate;

  /**
   * The index of the point
   */
  index: number;

  /**
   * The shape that the point belongs to
   */
  shape: Shape | DraftShape;

  /**
   * Shape id reference when available
   */
  shapeId?: string;

  /**
   * Indicates if the shape is a draft shape
   */
  isDraft: boolean;

  /**
   * Origin position in meters
   */
  origin: Point2D;

  /**
   * New position in meters
   */
  position: Point2D;

  /**
   * Difference from origin in meters
   */
  offset: Point2D;

  /**
   * Distance in meters
   */
  distance: number;

  /**
   * Snap angle in degrees when snapping is applied
   */
  snapAngle?: number;

  /**
   * Elevation in meters for the point, if available
   */
  elevation?: number;

  /**
   * Indicates that the payload represents a point move.
   */
  storeyId: Storey['id'];

  /**
   * Indicates that the payload represents a provisional insert point.
   * Used for rendering temporary geometry during drag.
   */
  isInsertDrag?: boolean;

  /**
   * Identifier of the temporary placeholder feature associated with
   * an insert drag. Ensures map sources can track the provisional point.
   */
  insertPlaceholderId?: string;
  /**
   * Keep existing multi-point selection when drag starts from a selected segment.
   */
  preserveMultiPointSelection?: boolean;
  /**
   * True when the drag started on the line between two adjacent vertices.
   * Segment drags keep the neighboring edge lines fixed while moving the
   * selected edge parallel to itself.
   */
  isLineSegmentDrag?: boolean;
  /** Endpoint indexes carried by a segment drag without selecting the points. */
  lineSegmentPointIndexes?: readonly [number, number];
  /** Alt/Option on macOS or Alt on Windows: offset every polygon edge. */
  uniformPolygonOffset?: boolean;
  /** Segment-drag perpendicular movement increment in meters. */
  segmentTranslationSnapMeters?: number;
  /** Shift+Cmd/Ctrl segment drag affects only the edited storey. */
  suppressUpwardPropagation?: boolean;
  /**
   * Indicates whether Shift was held when the interaction started.
   * Used to toggle point selection without clearing existing selected points.
   */
  isShiftKeyPressed?: boolean;
  /** Cmd on macOS or Ctrl on Windows was held for this pointer event. */
  isMetaOrCtrlKeyPressed?: boolean;
  /**
   * Explicit storey levels that should mirror this interaction.
   * Only used for insert drags where the affected segment is validated
   * against upper storeys.
   */
  linkedStoreyLevels?: number[];
  /**
   * Optional mapping of storey level to insert index for multi-level inserts.
   * Ensures upper storeys mutate the correct point index during live drags.
   */
  linkedInsertIndexes?: Record<number, number>;
  /**
   * Optional mapping of storey level to the matching shape id when ids differ
   * across duplicated storeys. Enables live drag rendering across levels.
   */
  linkedShapeIds?: Record<number, string>;
  /**
   * Epoch timestamp when the current drag interaction started.
   * Used to distinguish quick click-selection from an intentional drag.
   */
  dragStartedAtMs?: number;
  /**
   * Screen position at pointer down. Used with {@link screenPoint} at release
   * to treat as drag when movement exceeds {@link CLICK_DRAG_DISTANCE_THRESHOLD_PX}.
   */
  dragStartScreenPoint?: Point2D;
};

export type DraftPreviewPayload = {
  /**
   * Screen position of the tentative point in pixel coordinates
   */
  screenPoint: Point2D;

  /**
   * Distance in meters from the previous point in the draft chain
   */
  distance: number;

  /**
   * Snapped position in metric coordinates (after snap logic applied)
   */
  snappedPosition?: Point2D;

  /**
   * Draft chain points in metric coordinates
   */
  draftPoints?: Point2D[];

  /** Preset solid and courtyard cutouts at the live pointer or rotation center. */
  templateShapes?: DraftShape[];
  /** Placement fails the same validation used when committing the preset. */
  templatePlacementInvalid?: boolean;
  /** Projected preset placement center, including snapping and storey elevation. */
  templateCenterScreenPoint?: Point2D;

  /**
   * Geographic coordinate of the snapped position
   */
  snappedCoordinate?: Coordinate;

  /**
   * Last already-placed draft segment (for a muted length label at its midpoint).
   */
  lastCommittedSegment?: {
    screenPoint: Point2D;
    distanceMeters: number;
  };
};

export enum MapTooltipType {
  Distance = 'distance',
  Action = 'action',
}

export type ShapeTranslateTargetPayload = {
  storeyId: Storey['id'];
  shapeIds: Shape['id'][];
  storeyLevel: number;
};

export type ShapeTranslatePayload = {
  /** Alt-drag previews a copy while retaining the source building. */
  duplicateBuilding?: boolean;
  /**
   * Identifier of the primary translated shape
   */
  shapeId: Shape['id'];
  /**
   * Identifier of the base storey that initiated the translation
   */
  baseStoreyId: Storey['id'];
  /**
   * Explicit translation targets grouped by storey
   */
  targets: ShapeTranslateTargetPayload[];
  /**
   * Translation delta expressed in metric space
   */
  delta: Point2D;
  /** Optional whole-building rotation around a metric-space center. */
  rotation?: {
    center: Point2D;
    radians: number;
  };
  /** Render the pickup outline from the storey at, or immediately below, ground. */
  groundLevelOriginOutline?: boolean;
  /**
   * Optional cursor position for UI consumers
   */
  screenPoint?: Point2D;
};

export type BuildingRotationCursorState = {
  hover: boolean;
  dragging: boolean;
};

export type MapTooltipSegmentRef = {
  shapeId: string;
  startIndex: number;
  endIndex: number;
  lengthMeters: number;
  /** Projected endpoints of the rendered selected-storey segment. */
  screenStart: Point2D;
  screenEnd: Point2D;
  /** True when increasing point indexes traverse the polygon clockwise. */
  pointsAreClockwise: boolean;
};

export type MapTooltipCornerAngleRef = {
  shapeId: string;
  pointIndex: number;
  angleDegrees: number;
  /** True when increasing point indexes traverse the polygon clockwise. */
  pointsAreClockwise: boolean;
};

export type MapGeometryMeasurementFocus = {
  kind: 'segment' | 'corner-angle';
  shapeId: Shape['id'];
};

export type MapTooltipPayload = {
  type: MapTooltipType;
  screenPoint: Point2D;
  text: string;
  disabled?: boolean;
  /**
   * Visual weight.
   * - `muted`: reserved for low-emphasis labels
   * - `calibrate`: blueprint calibrate measure (matches calibrate line color)
   * - `segment-length`: transparent exterior polygon-edge measurement
   */
  tone?: 'default' | 'muted' | 'calibrate' | 'segment-length';
  /**
   * Placement relative to {@link screenPoint}.
   * - `cursor` (default): offset beside the pointer
   * - `center`: centered on the anchor (selected segment / calibrate)
   */
  anchor?: 'cursor' | 'center' | 'right';
  /**
   * When set, the label is a committed segment length that can be clicked to edit.
   */
  segment?: MapTooltipSegmentRef;
  /** When set, the label is a committed corner angle that can be edited. */
  cornerAngle?: MapTooltipCornerAngleRef;
  storeyHeight?: {
    storeyId: string;
    heightMeters: number;
    nextStoreyId?: string;
    previousStoreyId?: string;
  };
  selected?: boolean;
};

export type DraftClickPayload =
  | {
      type: 'add';
      preventDefault: () => void;
      position: Point2D;
      coordinate?: Coordinate;
      /** Location before empty-site rebase, used to roll back a failed persist. */
      previousLocation?: Coordinate;
      cutoutMode?: boolean;
      /** Shift held — all design snapping is disabled for this click. */
      isShiftKeyPressed?: boolean;
      /**
       * First empty-design click rebased the building location to this vertex
       * (local origin becomes [0,0]). Omit when a blueprint underlay is visible
       * so the plan stays put and the vertex keeps its click position.
       */
      rebasedBuildingLocation?: boolean;
      /**
       * When rebasing with a visible underlay, translate the underlay by this
       * local-meter offset so the image stays put in the world.
       */
      blueprintUnderlayOffset?: Point2D;
    }
  | {
      type: 'complete';
      preventDefault: () => void;
      reason: 'first-point' | 'close-distance';
    };

export type LineClickPayload = {
  preventDefault: () => void;
  shape: Shape;
  shapeId: string;
  elevation: number;
  lng: number;
  lat: number;
  startIndex: number;
  endIndex: number;
  startPoint?: Point2D;
  endPoint?: Point2D;
};

export type PointMeta = {
  storeyId?: Storey['id'];
  shape: Shape | DraftShape;
  shapeId?: string;
  isDraft: boolean;
  index: number;
  origin: Point2D;
  elevation?: number;
};

export type LineMeta = {
  shape: Shape;
  shapeId: string;
  elevation: number;
};

export type MapSelections = {
  storeyId?: Storey['id'];
  pointIndexes?: number[];
  shapeId?: Shape['id'];
  draftShape: boolean;
};

export type MapSelectionObservables = {
  storeyId$: BehaviorSubject<MapSelections['storeyId']>;
  pointIndexes$: BehaviorSubject<MapSelections['pointIndexes']>;
  shapeId$: BehaviorSubject<MapSelections['shapeId']>;
  draftShape$: BehaviorSubject<MapSelections['draftShape']>;
};

export type MapObservableCollection = {
  point: {
    mouse: {
      down$: Observable<PointPayload>;
      downMove$: Observable<PointPayload>;
      up$: Observable<PointPayload>;
      over$: Observable<PointPayload>;
      leave$: Observable<PointPayload>;
      doubleClick$: Observable<PointPayload>;
    };
    key: {
      backspace$: Observable<KeyboardEvent>;
      escape$: Observable<KeyboardEvent>;
      /** Tab key: cycle selected storey upward, wrapping from top to bottom. */
      tab$: Observable<KeyboardEvent>;
      /** Cmd/Ctrl+1–9: select storey by 1-based index from bottom. */
      storeyIndex$: Observable<KeyboardEvent>;
    };
  };

  draft: {
    mouse: {
      click$: Observable<DraftClickPayload>;
    };
    key: {
      escape$: Observable<KeyboardEvent>;
    };
  };

  map: {
    pitch$: Observable<number>;
    zoom$: Observable<number>;
  };

  line: {
    mouse: {
      click$: Observable<LineClickPayload>;
      doubleClick$: Observable<PointPayload>;
    };
  };

  selection: MapSelectionObservables;
};

/**
 * Current working state in the map.
 */
export type MapStateObservables = {
  storeys$: BehaviorSubject<Storey[]>;
  /**
   * Original storeys snapshot for boundary enforcement.
   * Used by {@link SnapAndBoundaries} to prevent crossing existing shape edges.
   */
  originalStoreys$: BehaviorSubject<Storey[]>;
  draftShape$: BehaviorSubject<DraftShape | undefined>;
  selectedStoreyId$: BehaviorSubject<Storey['id'] | undefined>;
  location$: BehaviorSubject<Coordinate>;
  locationPinMode$: BehaviorSubject<boolean>;
  /**
   * Pen-tool mode: when active, map clicks draw a new building footprint
   * regardless of the Alt modifier; completing the polygon creates a new
   * building (DEV-2576).
   */
  penMode$: BehaviorSubject<boolean>;
  /**
   * Non-selected buildings, rendered as static footprints so multiple
   * buildings stay visible while one is edited (DEV-2576).
   */
  otherBuildings$: BehaviorSubject<Building[]>;
  /**
   * False while no building is selected — a map background click cleared the
   * selection, so every building renders as a static footprint (DEV-2576).
   */
  hasBuildingSelection$: BehaviorSubject<boolean>;
  /**
   * True while the whole building is selected (map footprint click): the
   * volume gets a blue outline and no storey is active (DEV-2576).
   */
  isBuildingOutlineSelected$: BehaviorSubject<boolean>;
  /** Static sibling building under the pointer, outlined without selecting it. */
  hoveredBuildingId$: BehaviorSubject<string | undefined>;
  hoveredStoreyId$: BehaviorSubject<string | undefined>;
  storeyHeightFocus$: Subject<string>;
};

export enum MapHoverStateType {
  Point = 'point',
  DraftPoint = 'draft-point',
  Line = 'line',
  DraftLine = 'draft-line',
  Shape = 'shape',
}

export type MapHoverStatePoint = {
  type: MapHoverStateType.Point;
  index: number;
  shapeId: string;
  screenPoint?: Point2D;
};

export type MapHoverStateDraftPoint = {
  type: MapHoverStateType.DraftPoint;
  index: number;
  shapeId?: string;
  canComplete?: boolean;
  screenPoint?: Point2D;
};

export type MapHoverStateLine = {
  type: MapHoverStateType.Line;
  startIndex: number;
  endIndex: number;
  shapeId: string;
  screenPoint?: Point2D;
  lengthMeters?: number;
  insertCandidateActive?: boolean;
  midpoint?: Point2D;
  midpointCoordinate?: Coordinate;
};

export type MapHoverStateDraftLine = {
  type: MapHoverStateType.DraftLine;
  startIndex: number;
  endIndex: number;
  screenPoint?: Point2D;
};

export type MapHoverStateShape = {
  type: MapHoverStateType.Shape;
  shapeId: string;
  screenPoint?: Point2D;
};

export type MapHoverState =
  | MapHoverStatePoint
  | MapHoverStateLine
  | MapHoverStateDraftPoint
  | MapHoverStateDraftLine
  | MapHoverStateShape;

export type MapDraftCursorState = {
  active: boolean;
  suppressed: boolean;
};
